/**
 * rpm.util — the RPM Plan's DISPLAY rules, in one place for the plan and both
 * MRP screens: which day "today" is, which Sunday a week starts on, how an
 * estimate reads, and the tag a status or a week's outcome wears.
 *
 * WHAT IS NOT HERE, ON PURPOSE. Overdue, a block's status after a tick, the
 * total estimate, which blocks are on the plan, a past week's outcomes and
 * counts, and the year summary are all the SERVER'S answers now (the RPM API,
 * `docs/backend-spec-rpm-plan.md` §4.2–§4.7). This file used to work every one
 * of them out while the screen ran on local data; that copy was deleted when
 * the API was wired, so there is exactly one place each rule lives and it is
 * the backend.
 *
 * TIME ZONE (D14). "Today" and the week boundary are read in Mountain Time,
 * {@link COMPANY_TIME_ZONE} — the zone the server reads them in — never UTC and
 * never the browser's own zone, so the week the navigator asks for is the week
 * the server means.
 */
import { RpmItemStatus, RpmWeekOutcome } from '@core/models';
import { COMPANY_TIME_ZONE, fromIsoDate, isoDateInZone, startOfWeek, toIsoDate } from '@core/util/date.util';

/** Today's calendar day in Mountain Time. */
export function rpmToday(now: Date = new Date()): string {
  return isoDateInZone(now, COMPANY_TIME_ZONE);
}

/**
 * The Sunday that starts the week a calendar day falls in — the `{sunday}` the
 * week read takes (it refuses any other day). Goes through the app's one
 * `startOfWeek`, because a week runs Sunday to Saturday everywhere (D4).
 */
export function rpmWeekStart(isoDate: string): string {
  return toIsoDate(startOfWeek(fromIsoDate(isoDate) ?? new Date())) ?? isoDate;
}

/** "45 min", "2 h", "2 h 30 min" — an estimate, readable at a glance. */
export function formatEstimate(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** The tag a status wears — one wording and colour for the plan and both MRP screens. */
export function rpmStatusTag(status: RpmItemStatus): { label: string; tone: string } {
  if (status === 'DONE') return { label: 'Done', tone: 'emerald' };
  if (status === 'IN_PROGRESS') return { label: 'In progress', tone: 'amber' };
  return { label: 'To do', tone: 'slate' };
}

/** The tag a past week's outcome wears in the history. */
export function rpmOutcomeTag(outcome: RpmWeekOutcome): { label: string; tone: string } {
  if (outcome === 'COMPLETED') return { label: 'Completed', tone: 'emerald' };
  if (outcome === 'DELETED') return { label: 'Deleted', tone: 'slate' };
  if (outcome === 'OVERDUE') return { label: 'Overdue', tone: 'rose' };
  return { label: 'Open', tone: 'sky' };
}
